import React, { useState, useEffect, useRef } from 'react';
import { X, Plus, CreditCard as Edit2, Trash2, Eye, Search, Users, Filter, ChevronDown, ChevronUp, Clipboard, Home, Flame, Phone, PhoneCall, Target, XCircle, Clock, Zap, FileText, Maximize2 } from 'lucide-react';
import { auth, db } from '../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import toast from 'react-hot-toast';
import type { Client, FormData, PropertyClientMatch } from '../types';
import { normalizeCityName, normalizeCityArray } from '../utils/cityNormalization';
import AddClientModal from './AddClientModal';
import AddActivityModal from './AddActivityModal';
import FloatingClientCard from './FloatingClientCard';
import ClientPropertiesModal from './ClientPropertiesModal';
import { getLeadRecommendation } from '../utils/leadRecommendation';
import MessageDraftModal, { GeneratingModal } from './MessageDraftModal';
import ManuallyMatchedPropertiesModal from './ManuallyMatchedPropertiesModal';
import SellerPropertiesModal from './SellerPropertiesModal';
import { cities } from '../data';

function formatLocalDate(utcString: string | null | undefined): string | null {
  if (!utcString) return null;
  const d = new Date(utcString);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const activityTypeLabels: Record<string, string> = {
  call: 'שיחה',
  whatsapp: 'WhatsApp',
  meeting: 'פגישה',
  property_sent: 'שליחת נכס',
  note: 'הערה',
};

function formatLastActivity(activity: { activityType: string; createdAt: string } | null | undefined): string | null {
  if (!activity) return null;
  const label = activityTypeLabels[activity.activityType] || activity.activityType;
  const d = new Date(activity.createdAt);
  const now = new Date();
  const pad = (n: number) => n.toString().padStart(2, '0');
  const isToday = d.toDateString() === now.toDateString();
  const time = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  const dateStr = isToday ? 'היום' : `${pad(d.getDate())}/${pad(d.getMonth() + 1)}`;
  return `${label} – ${dateStr} ${time}`;
}

interface ClientCRMProps {
  onClose: () => void;
  selectedLeadId?: string;
  autoOpenMatchLeadId?: string;
}

const propertyTypes = [
  { value: 'apartment', label: 'דירת מגורים' },
  { value: 'duplex', label: 'דופלקס' },
  { value: 'penthouse', label: 'פנטהאוז' },
  { value: 'garden-apartment', label: 'דירת גן' },
  { value: 'house', label: 'בית פרטי' },
  { value: 'commercial', label: 'מסחרי' },
];

const roomOptions = [1, 2, 3, 4, 5, 6];

const customerStatusLabels: Record<string, { label: string; color: string }> = {
  new: { label: 'חדש', color: 'bg-sky-600' },
  in_progress: { label: 'בטיפול', color: 'bg-yellow-600' },
  available: { label: 'זמין', color: 'bg-green-600' },
  unavailable: { label: 'לא זמין', color: 'bg-gray-500' },
  serious: { label: 'רציני', color: 'bg-emerald-600' },
  met: { label: 'ניפגש', color: 'bg-cyan-600' },
  property_visit: { label: 'ביקור בנכס', color: 'bg-teal-600' },
  deal_closed: { label: 'נסגרה עסקה', color: 'bg-green-700' },
  closed: { label: 'נסגרה עסקה', color: 'bg-green-700' },
  not_relevant: { label: 'לא רלוונטי', color: 'bg-red-600' },
};

const customerStatusFilterOptions = Object.entries(customerStatusLabels)
  .filter(([key]) => key !== 'closed')
  .map(([value, { label }]) => ({ value, label }));

const ClientCRM: React.FC<ClientCRMProps> = ({ onClose, selectedLeadId, autoOpenMatchLeadId }) => {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [activityClient, setActivityClient] = useState<Client | null>(null);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [selectedClientForManual, setSelectedClientForManual] = useState<Client | null>(null);
  const [selectedSeller, setSelectedSeller] = useState<Client | null>(null);
  const [expandedCardId, setExpandedCardId] = useState<string | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [filterLeadType, setFilterLeadType] = useState<'all' | 'buyer' | 'seller'>('all');
  const [filterCities, setFilterCities] = useState<string[]>([]);
  const [filterRooms, setFilterRooms] = useState<number[]>([]);
  const [filterPropertyTypes, setFilterPropertyTypes] = useState<string[]>([]);
  const [filterCustomerStatus, setFilterCustomerStatus] = useState<string[]>([]);
  const [activeWorkMode, setActiveWorkMode] = useState<string | null>(null);

  const [filterCreatedAt, setFilterCreatedAt] = useState<string>('all');
  const [filterCreatedFrom, setFilterCreatedFrom] = useState('');
  const [filterCreatedTo, setFilterCreatedTo] = useState('');

  const [generatingMessageFor, setGeneratingMessageFor] = useState<Client | null>(null);
  const [messageDraft, setMessageDraft] = useState<{ draft: any; leadName: string; client: Client } | null>(null);

  type MessageGender = 'male' | 'female' | 'neutral';
  interface MessageGenConfig {
    client: Client;
    gender: MessageGender;
    sellerProperties: FormData[];
    selectedFormId: string | null;
    loadingProperties: boolean;
  }
  const [messageGenConfig, setMessageGenConfig] = useState<MessageGenConfig | null>(null);

  const leadRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const [highlightedLeadId, setHighlightedLeadId] = useState<string | null>(null);
  const sortOrderRef = useRef<string[]>([]);

  const [isOfficeView, setIsOfficeView] = useState(false);
  const [filterAgentId, setFilterAgentId] = useState<string>('all');

  useEffect(() => {
    loadClients();
  }, []);

  useEffect(() => {
    if (!selectedLeadId || clients.length === 0) return;

    const targetClient = clients.find((c) => c.id === selectedLeadId);
    if (!targetClient) {
      setFilterLeadType('seller');
      toast.error('לא נמצא כרטיס המוכר הספציפי');
      return;
    }

    if (targetClient.leadType === 'seller') setFilterLeadType('seller');
    else if (targetClient.leadType === 'buyer') setFilterLeadType('buyer');

    setExpandedCardId(selectedLeadId);

    setTimeout(() => {
      const element = leadRefs.current[selectedLeadId];
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setHighlightedLeadId(selectedLeadId);
        setTimeout(() => setHighlightedLeadId(null), 3000);
      }
    }, 300);
  }, [selectedLeadId, clients]);

  // Auto-open the matches modal for a specific lead (triggered by Smart Alert click)
  useEffect(() => {
    if (!autoOpenMatchLeadId || clients.length === 0) return;

    const targetClient = clients.find((c) => c.id === autoOpenMatchLeadId);
    if (!targetClient) return;

    if (targetClient.leadType === 'seller') setFilterLeadType('seller');
    else if (targetClient.leadType === 'buyer') setFilterLeadType('buyer');

    setExpandedCardId(autoOpenMatchLeadId);
    setSelectedClient(targetClient);

    setTimeout(() => {
      const element = leadRefs.current[autoOpenMatchLeadId];
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setHighlightedLeadId(autoOpenMatchLeadId);
        setTimeout(() => setHighlightedLeadId(null), 3000);
      }
    }, 300);
  }, [autoOpenMatchLeadId, clients]);

  useEffect(() => {
    if (clients.length > 0 && sortOrderRef.current.length === 0) {
      const computeScore = (client: Client): number => {
        let score = 0;
        const status = client.customerStatus || 'new';
        if (status === 'serious') score += 50;
        if (status === 'property_visit') score += 40;
        if (client.nextFollowUpAt && new Date(client.nextFollowUpAt).getTime() < Date.now()) score += 30;
        if (client.lastContactAt && new Date(client.lastContactAt).getTime() < Date.now() - 3 * 24 * 60 * 60 * 1000) score += 20;
        if (!client.lastContactAt) score += 20;
        if (status === 'new') score += 10;
        if (status === 'not_relevant') score -= 50;
        if (status === 'deal_closed' || status === 'closed') score -= 100;
        return score;
      };
      const sorted = [...clients].sort((a, b) => computeScore(b) - computeScore(a));
      sortOrderRef.current = sorted.map(c => c.id);
    }
  }, [clients]);

  const loadClients = async () => {
    if (!auth.currentUser) return;

    try {
      setLoading(true);

      const idToken = await auth.currentUser.getIdToken();
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const apiUrl = `${supabaseUrl}/functions/v1/get-leads`;

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch leads');
      }

      const data = await response.json();

      sortOrderRef.current = [];
      setClients(data.leads);
      setIsOfficeView(data.isOfficeView ?? false);
    } catch (error) {
      console.error('Error loading clients:', error);
      toast.error('שגיאה בטעינת הלקוחות');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClient = async (clientId: string) => {
    if (!confirm('האם אתה בטוח שברצונך למחוק לקוח זה?')) return;

    try {
      const idToken = await auth.currentUser?.getIdToken();
      if (!idToken) {
        toast.error('שגיאה באימות');
        return;
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const apiUrl = `${supabaseUrl}/functions/v1/delete-lead?leadId=${clientId}`;

      const response = await fetch(apiUrl, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete lead');
      }

      setClients(prev => prev.filter(c => c.id !== clientId));
      toast.success('הלקוח נמחק בהצלחה');
    } catch (error) {
      console.error('Error deleting client:', error);
      toast.error('שגיאה במחיקת הלקוח');
    }
  };

  const handleMarkContacted = async (clientId: string) => {
    try {
      const idToken = await auth.currentUser?.getIdToken();
      if (!idToken) {
        toast.error('שגיאה באימות');
        return;
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const apiUrl = `${supabaseUrl}/functions/v1/update-lead`;
      const now = new Date().toISOString();

      const response = await fetch(apiUrl, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          leadId: clientId,
          lastContactAt: now,
          nextFollowUpAt: null,
          nextAction: null,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to mark contact');
      }

      setClients(prev => prev.map(c =>
        c.id === clientId ? { ...c, lastContactAt: now, lastActivityAt: now, nextFollowUpAt: null, nextAction: null } : c
      ));
      toast.success('סומן שנוצר קשר');
    } catch (error) {
      console.error('Error marking contact:', error);
      toast.error('שגיאה בעדכון');
    }
  };

  const fetchMatchingProperties = async (client: Client): Promise<{ city: string; address: string; rooms: number; price: number; size: number; shortDescription: string }[]> => {
    if (!auth.currentUser || client.leadType !== 'buyer') return [];

    try {
      // 1. Check for manually matched properties first
      const matchesRef = collection(db, 'property_client_matches');
      const matchesQuery = query(
        matchesRef,
        where('clientId', '==', client.id),
        where('userId', '==', auth.currentUser.uid)
      );
      const matchesSnapshot = await getDocs(matchesQuery);
      const manualFormIds = matchesSnapshot.docs
        .filter(d => d.data().status !== 'rejected')
        .map(d => d.data().formId);

      // 2. Fetch all agent forms
      const formsRef = collection(db, 'forms');
      const q = query(formsRef, where('userId', '==', auth.currentUser.uid));
      const snapshot = await getDocs(q);
      const allForms = snapshot.docs.map(d => ({ formId: d.id, ...d.data() })) as FormData[];

      let matchedForms: FormData[] = [];

      // 3. Prefer manually matched
      if (manualFormIds.length > 0) {
        matchedForms = allForms.filter(f => manualFormIds.includes(f.formId));
      }

      // 4. If no manual matches, auto-match by criteria
      if (matchedForms.length === 0) {
        const criteria = client.searchCriteria;
        if (criteria) {
          const tolerance = (criteria.priceTolerance || 5) / 100;
          matchedForms = allForms.filter(prop => {
            if (!prop.property || !prop.address) return false;
            const propertyCity = normalizeCityName(prop.address.city);
            if (criteria.priceMin && prop.property.price < criteria.priceMin * (1 - tolerance)) return false;
            if (criteria.priceMax && prop.property.price > criteria.priceMax * (1 + tolerance)) return false;
            if (criteria.rooms?.length && !criteria.rooms.includes(prop.property.rooms)) return false;
            if (criteria.cities?.length) {
              const normalizedCriteriaCities = normalizeCityArray(criteria.cities);
              if (!normalizedCriteriaCities.includes(propertyCity || '')) return false;
            }
            if (criteria.propertyTypes?.length && !criteria.propertyTypes.includes(prop.property.propertyType)) return false;
            if (criteria.minSize && prop.property.size < criteria.minSize) return false;
            if (criteria.maxSize && prop.property.size > criteria.maxSize) return false;
            return true;
          });
        }
      }

      // 5. Deduplicate by formId, then by address+city+price
      const seen = new Set<string>();
      const dedupedForms: FormData[] = [];
      for (const f of matchedForms) {
        const key = f.formId || `${f.address?.street}-${f.address?.city}-${f.property?.price}`;
        if (!seen.has(key)) {
          seen.add(key);
          dedupedForms.push(f);
        }
      }

      // 6. Format top 3
      const propertyTypeLabels: Record<string, string> = {
        apartment: 'דירה', duplex: 'דופלקס', penthouse: 'פנטהאוז',
        'garden-apartment': 'דירת גן', house: 'בית פרטי', commercial: 'מסחרי',
      };

      return dedupedForms.slice(0, 3).map(f => {
        const cityLabel = cities.find(c => c.value === f.address.city)?.label || f.address.city;
        const neighborhood = f.address.neighborhood || '';
        const street = f.address.street || '';
        const addr = [street, neighborhood].filter(Boolean).join(', ');
        const description = f.property.description
          ? f.property.description.slice(0, 60)
          : (propertyTypeLabels[f.property.propertyType] || f.property.propertyType);
        return {
          city: cityLabel,
          address: addr || cityLabel,
          rooms: f.property.rooms,
          price: f.property.price,
          size: f.property.size || 0,
          shortDescription: description,
        };
      });
    } catch (err) {
      console.error('Error fetching matching properties:', err);
      return [];
    }
  };

  const getAgentName = async (): Promise<string> => {
    if (!auth.currentUser) return '';
    try {
      const userDoc = await getDocs(query(collection(db, 'users'), where('email', '==', auth.currentUser.email)));
      if (!userDoc.empty) {
        return userDoc.docs[0].data().fullName || '';
      }
    } catch {
      // fallback
    }
    return auth.currentUser.displayName || '';
  };

  const computeLeadTemperature = (client: Client): 'hot' | 'medium' | 'cold' => {
    const hotStatuses = ['serious', 'met', 'property_visit'];
    const status = client.customerStatus || 'new';

    if (hotStatuses.includes(status)) return 'hot';

    const lastContact = client.lastContactAt || client.lastActivityAt;
    if (lastContact) {
      const daysSince = (Date.now() - new Date(lastContact).getTime()) / (1000 * 60 * 60 * 24);
      if (daysSince <= 3 && status !== 'new') return 'hot';
      if (daysSince > 14) return 'cold';
    }

    if (status === 'new' && !client.lastActivity) return 'cold';
    if (status === 'unavailable' || status === 'not_relevant') return 'cold';

    return 'medium';
  };

  const openMessageConfig = async (client: Client) => {
    if (client.leadType === 'seller') {
      setMessageGenConfig({ client, gender: 'neutral', sellerProperties: [], selectedFormId: null, loadingProperties: true });
      try {
        const idToken = await auth.currentUser?.getIdToken();
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const res = await fetch(`${supabaseUrl}/functions/v1/get-seller-properties?sellerId=${client.id}`, {
          headers: { 'Authorization': `Bearer ${idToken}` },
        });
        const data = await res.json();
        const formIds: string[] = (data.properties || []).map((p: any) => p.form_id);
        const propertiesData: FormData[] = [];
        for (const formId of formIds) {
          const snap = await getDocs(query(collection(db, 'forms'), where('__name__', '==', formId)));
          if (!snap.empty) {
            const d = snap.docs[0];
            const dd = d.data();
            propertiesData.push({ formId: d.id, userId: dd.userId, createdAt: dd.createdAt || new Date().toISOString(), version: dd.version || 1, status: dd.status || 'completed', address: dd.address || {}, property: dd.property || {}, imageUrls: dd.imageUrls || { propertyImages: [] }, nearbyPlaces: dd.nearbyPlaces || {} } as FormData);
          }
        }
        setMessageGenConfig(prev => prev ? { ...prev, sellerProperties: propertiesData, selectedFormId: propertiesData.length === 1 ? propertiesData[0].formId : null, loadingProperties: false } : null);
      } catch {
        setMessageGenConfig(prev => prev ? { ...prev, loadingProperties: false } : null);
      }
    } else {
      setMessageGenConfig({ client, gender: 'neutral', sellerProperties: [], selectedFormId: null, loadingProperties: false });
    }
  };

  const handleGenerateMessage = async (client: Client, gender: 'male' | 'female' | 'neutral' = 'neutral', selectedProperty?: FormData | null) => {
    try {
      setGeneratingMessageFor(client);

      const idToken = await auth.currentUser?.getIdToken();
      if (!idToken) {
        toast.error('שגיאה באימות');
        setGeneratingMessageFor(null);
        return;
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const apiUrl = `${supabaseUrl}/functions/v1/generate-lead-message`;

      const criteria = client.searchCriteria;
      const searchCriteria = criteria ? {
        cities: criteria.cities || [],
        rooms: criteria.rooms || [],
        budgetMin: criteria.priceMin || 0,
        budgetMax: criteria.priceMax || 0,
      } : null;

      const matchingProperties = await fetchMatchingProperties(client);
      const agentName = await getAgentName();
      const leadTemperature = computeLeadTemperature(client);

      console.log('AI matchingProperties', matchingProperties);
      console.log('AI searchCriteria', searchCriteria);
      console.log('AI leadTemperature', leadTemperature);

      const lastActivitySummary = client.lastActivity
        ? `${client.lastActivity.activityType} ב-${client.lastActivity.createdAt}`
        : null;

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          leadId: client.id,
          fullName: client.name,
          email: client.email || '',
          leadType: client.leadType || 'buyer',
          customerStatus: client.customerStatus || 'new',
          lastContactAt: client.lastContactAt,
          nextFollowUpAt: client.nextFollowUpAt,
          nextAction: client.nextAction,
          lastActivitySummary,
          notes: client.notes,
          searchCriteria,
          matchingProperties,
          agentName,
          leadTemperature,
          gender,
          sellerProperty: selectedProperty ? {
            city: selectedProperty.address?.city || '',
            address: [selectedProperty.address?.street, selectedProperty.address?.houseNumber].filter(Boolean).join(' '),
            price: selectedProperty.property?.price,
            rooms: selectedProperty.property?.rooms,
            size: selectedProperty.property?.size,
            description: selectedProperty.property?.description,
          } : undefined,
        }),
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || 'Failed to generate message');
      }

      const data = await response.json();
      setGeneratingMessageFor(null);

      if (data.draft === null) {
        toast('ליד לא רלוונטי — לא נוצרה הודעה', { icon: 'ℹ️' });
        return;
      }

      setMessageDraft({ draft: data.draft, leadName: client.name, client });
    } catch (error) {
      console.error('Error generating message:', error);
      toast.error('שגיאה ביצירת ההודעה');
      setGeneratingMessageFor(null);
    }
  };

  const clearFilters = () => {
    setFilterLeadType('all');
    setFilterCities([]);
    setFilterRooms([]);
    setFilterPropertyTypes([]);
    setFilterCustomerStatus([]);
    setFilterCreatedAt('all');
    setFilterCreatedFrom('');
    setFilterCreatedTo('');
    setActiveWorkMode(null);
    setFilterAgentId('all');
  };

  const toggleCity = (city: string) => {
    setFilterCities(prev =>
      prev.includes(city) ? prev.filter(c => c !== city) : [...prev, city]
    );
  };

  const togglePropertyType = (type: string) => {
    setFilterPropertyTypes(prev =>
      prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
    );
  };

  const toggleCustomerStatus = (status: string) => {
    setActiveWorkMode(null);
    setFilterCustomerStatus(prev =>
      prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]
    );
  };

  const applyWorkMode = (mode: string) => {
    if (activeWorkMode === mode) {
      setActiveWorkMode(null);
      setFilterCustomerStatus([]);
      return;
    }
    setActiveWorkMode(mode);
    setFilterCustomerStatus([]);
  };

  const activeFiltersCount =
    (filterLeadType !== 'all' ? 1 : 0) +
    (filterCities.length > 0 ? 1 : 0) +
    (filterRooms.length > 0 ? 1 : 0) +
    (filterPropertyTypes.length > 0 ? 1 : 0) +
    (filterCustomerStatus.length > 0 ? 1 : 0) +
    (filterCreatedAt !== 'all' ? 1 : 0) +
    (activeWorkMode ? 1 : 0) +
    (filterAgentId !== 'all' ? 1 : 0);

  const computePriorityScore = (client: Client): number => {
    let score = 0;
    const status = client.customerStatus || 'new';
    if (status === 'serious') score += 50;
    if (status === 'property_visit') score += 40;
    if (client.nextFollowUpAt && new Date(client.nextFollowUpAt).getTime() < Date.now()) score += 30;
    if (client.lastContactAt && new Date(client.lastContactAt).getTime() < Date.now() - 3 * 24 * 60 * 60 * 1000) score += 20;
    if (!client.lastContactAt) score += 20;
    if (status === 'new') score += 10;
    if (status === 'not_relevant') score -= 50;
    if (status === 'deal_closed' || status === 'closed') score -= 100;
    return score;
  };

  const getPriorityLevel = (score: number): { label: string; color: string } => {
    if (score >= 50) return { label: 'גבוה', color: 'bg-red-600 text-white' };
    if (score >= 20) return { label: 'בינוני', color: 'bg-yellow-600 text-white' };
    return { label: 'נמוך', color: 'bg-gray-600 text-gray-300' };
  };

  const filteredClients = clients.filter(client => {
    const matchesSearch =
      client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.phone.includes(searchTerm) ||
      client.email?.toLowerCase().includes(searchTerm.toLowerCase());

    if (!matchesSearch) return false;

    if (filterLeadType !== 'all' && client.leadType !== filterLeadType) {
      return false;
    }

    if (filterCities.length > 0) {
      const clientCities = client.searchCriteria.cities || [];
      const hasMatchingCity = clientCities.some(city => filterCities.includes(city));
      if (!hasMatchingCity) return false;
    }

    if (filterRooms.length > 0) {
      const clientRooms = client.searchCriteria.rooms || [];
      const hasMatchingRooms = clientRooms.some(room => filterRooms.includes(room));
      if (!hasMatchingRooms) return false;
    }

    if (filterPropertyTypes.length > 0) {
      const clientPropertyTypes = client.searchCriteria.propertyTypes || [];
      const hasMatchingPropertyType = clientPropertyTypes.some(type => filterPropertyTypes.includes(type));
      if (!hasMatchingPropertyType) return false;
    }

    if (filterCustomerStatus.length > 0) {
      const clientStatus = client.customerStatus || 'new';
      const normalizedStatus = clientStatus === 'closed' ? 'deal_closed' : clientStatus;
      if (!filterCustomerStatus.includes(normalizedStatus)) return false;
    }

    if (activeWorkMode) {
      const status = client.customerStatus || 'new';
      const threeDaysAgo = Date.now() - 3 * 24 * 60 * 60 * 1000;

      if (activeWorkMode === 'needs_action') {
        const statusMatch = ['new', 'in_progress', 'property_visit'].includes(status);
        const overdueFollowUp = client.nextFollowUpAt && new Date(client.nextFollowUpAt).getTime() < Date.now();
        const oldActivity = client.lastActivityAt
          ? new Date(client.lastActivityAt).getTime() < threeDaysAgo
          : new Date(client.createdAt).getTime() < threeDaysAgo;
        if (!statusMatch && !overdueFollowUp && !oldActivity) return false;
      } else if (activeWorkMode === 'no_contact') {
        const noContact = !client.lastContactAt;
        const oldContact = client.lastContactAt && new Date(client.lastContactAt).getTime() < threeDaysAgo;
        if (!noContact && !oldContact) return false;
      } else if (activeWorkMode === 'action_required') {
        const overdueFollowUp = client.nextFollowUpAt && new Date(client.nextFollowUpAt).getTime() < Date.now();
        const statusMatch = ['new', 'in_progress'].includes(status);
        const hasAction = !!client.nextAction;
        if (!overdueFollowUp && !statusMatch && !hasAction) return false;
      } else if (activeWorkMode === 'high_priority') {
        if (computePriorityScore(client) < 50) return false;
      } else if (activeWorkMode === 'serious') {
        if (!['serious', 'deal_closed', 'closed'].includes(status)) return false;
      } else if (activeWorkMode === 'not_relevant') {
        if (status !== 'not_relevant') return false;
      }
    }

    if (filterCreatedAt !== 'all') {
      const createdDate = new Date(client.createdAt);
      const now = new Date();
      const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());

      if (filterCreatedAt === 'today') {
        if (createdDate < startOfToday) return false;
      } else if (filterCreatedAt === '7days') {
        const sevenDaysAgo = new Date(startOfToday.getTime() - 7 * 24 * 60 * 60 * 1000);
        if (createdDate < sevenDaysAgo) return false;
      } else if (filterCreatedAt === '30days') {
        const thirtyDaysAgo = new Date(startOfToday.getTime() - 30 * 24 * 60 * 60 * 1000);
        if (createdDate < thirtyDaysAgo) return false;
      } else if (filterCreatedAt === 'custom') {
        if (filterCreatedFrom && createdDate < new Date(filterCreatedFrom)) return false;
        if (filterCreatedTo) {
          const toEnd = new Date(filterCreatedTo);
          toEnd.setDate(toEnd.getDate() + 1);
          if (createdDate >= toEnd) return false;
        }
      }
    }

    if (isOfficeView && filterAgentId !== 'all' && client.agentId !== filterAgentId) {
      return false;
    }

    return true;
  });

  const sortedFilteredClients = (() => {
    if (sortOrderRef.current.length === 0) {
      return [...filteredClients].sort((a, b) => computePriorityScore(b) - computePriorityScore(a));
    }
    return [...filteredClients].sort((a, b) => {
      const orderA = sortOrderRef.current.indexOf(a.id);
      const orderB = sortOrderRef.current.indexOf(b.id);
      if (orderA === -1 && orderB === -1) return computePriorityScore(b) - computePriorityScore(a);
      if (orderA === -1) return 1;
      if (orderB === -1) return -1;
      return orderA - orderB;
    });
  })();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50" dir="rtl">
      <div className="bg-gray-800 w-full h-full flex flex-col">
        {/* Fixed header */}
        <div className="shrink-0 px-5 py-4 border-b border-gray-700 bg-gradient-to-r from-blue-600 to-blue-700 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Users size={24} className="text-white" />
            <h2 className="text-xl font-bold text-white">Leads & Actions - CRM</h2>
            <span className="text-sm text-blue-100 opacity-80">({sortedFilteredClients.length})</span>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-colors"
          >
            <X size={22} />
          </button>
        </div>

        {/* Sticky search + add bar */}
        <div className="shrink-0 px-5 py-3 border-b border-gray-700 bg-gray-800 flex gap-3 items-center">
          <div className="flex-1 relative">
            <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="חפש לפי שם, טלפון או אימייל..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pr-10 pl-4 py-2.5 rounded-lg bg-gray-700 border border-gray-600 text-white placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-sm"
            />
          </div>
          <button
            onClick={() => setFiltersOpen(!filtersOpen)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg transition-colors text-sm font-medium ${
              filtersOpen || activeFiltersCount > 0
                ? 'bg-blue-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            <Filter size={16} />
            סינון לקוחות
            {activeFiltersCount > 0 && (
              <span className="bg-white bg-opacity-20 text-white text-xs px-1.5 py-0.5 rounded-full">
                {activeFiltersCount}
              </span>
            )}
            {filtersOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
          <button
            onClick={() => {
              setEditingClient(null);
              setShowAddModal(true);
            }}
            className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm font-medium whitespace-nowrap"
          >
            <Plus size={18} />
            קונה/מוכר חדש
          </button>
        </div>

        {/* Collapsible filters - compact chips UI */}
        {filtersOpen && (
          <div className="shrink-0 px-5 py-3 border-b border-gray-700 bg-gray-750 space-y-3">
            {/* Lead type + rooms row */}
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-xs text-gray-400 font-medium min-w-[60px]">סוג:</span>
              {(['all', 'buyer', 'seller'] as const).map(type => (
                <button
                  key={type}
                  onClick={() => setFilterLeadType(type)}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                    filterLeadType === type
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  {type === 'all' ? 'הכל' : type === 'buyer' ? 'קונה' : 'מוכר'}
                </button>
              ))}

              <span className="text-gray-600 mx-1">|</span>

              <span className="text-xs text-gray-400 font-medium min-w-[55px]">חדרים:</span>
              {roomOptions.map(room => (
                <button
                  key={room}
                  onClick={() => {
                    setFilterRooms(prev =>
                      prev.includes(room) ? prev.filter(r => r !== room) : [...prev, room]
                    );
                  }}
                  className={`w-7 h-7 rounded-full text-xs font-medium transition-all flex items-center justify-center ${
                    filterRooms.includes(room)
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  {room}
                </button>
              ))}
            </div>

            {/* Property types - chips */}
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-gray-400 font-medium min-w-[60px]">סוג נכס:</span>
              {propertyTypes.map(type => (
                <button
                  key={type.value}
                  onClick={() => togglePropertyType(type.value)}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                    filterPropertyTypes.includes(type.value)
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  {type.label}
                </button>
              ))}
            </div>

            {/* Customer status - chips */}
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-gray-400 font-medium min-w-[60px]">סטטוס:</span>
              {customerStatusFilterOptions.map(({ value, label }) => (
                <button
                  key={value}
                  onClick={() => toggleCustomerStatus(value)}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                    filterCustomerStatus.includes(value)
                      ? `${customerStatusLabels[value].color} text-white`
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Cities - chips (scrollable row) */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400 font-medium min-w-[60px] shrink-0">ערים:</span>
              <div className="flex flex-wrap gap-1.5 max-h-[52px] overflow-y-auto scrollbar-thin">
                {cities.map(city => (
                  <button
                    key={city.value}
                    onClick={() => toggleCity(city.value)}
                    className={`px-2.5 py-0.5 rounded-full text-xs font-medium transition-all whitespace-nowrap ${
                      filterCities.includes(city.value)
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    {city.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Creation date filter */}
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-gray-400 font-medium min-w-[60px]">תאריך כניסה:</span>
              {([
                { value: 'all', label: 'הכל' },
                { value: 'today', label: 'מהיום' },
                { value: '7days', label: '7 ימים' },
                { value: '30days', label: '30 ימים' },
                { value: 'custom', label: 'טווח מותאם' },
              ] as const).map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setFilterCreatedAt(opt.value)}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                    filterCreatedAt === opt.value
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
              {filterCreatedAt === 'custom' && (
                <div className="flex items-center gap-2 mr-2">
                  <input
                    type="date"
                    value={filterCreatedFrom}
                    onChange={e => setFilterCreatedFrom(e.target.value)}
                    className="bg-gray-700 border border-gray-600 rounded px-2 py-0.5 text-xs text-white"
                  />
                  <span className="text-xs text-gray-500">עד</span>
                  <input
                    type="date"
                    value={filterCreatedTo}
                    onChange={e => setFilterCreatedTo(e.target.value)}
                    className="bg-gray-700 border border-gray-600 rounded px-2 py-0.5 text-xs text-white"
                  />
                </div>
              )}
            </div>

            {/* Agent filter — visible to office manager/owner only */}
            {isOfficeView && (() => {
              const seen = new Set<string>();
              const agents: { id: string; name: string }[] = [];
              for (const c of clients) {
                if (c.agentId && !seen.has(c.agentId)) {
                  seen.add(c.agentId);
                  agents.push({ id: c.agentId, name: c.agentName || c.agentId });
                }
              }
              if (agents.length < 2) return null;
              return (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs text-gray-400 font-medium min-w-[60px]">סוכן:</span>
                  <button
                    onClick={() => setFilterAgentId('all')}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                      filterAgentId === 'all' ? 'bg-teal-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    כל הסוכנים
                  </button>
                  {agents.map(agent => (
                    <button
                      key={agent.id}
                      onClick={() => setFilterAgentId(agent.id)}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                        filterAgentId === agent.id ? 'bg-teal-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      }`}
                    >
                      {agent.name}
                    </button>
                  ))}
                </div>
              );
            })()}

            {/* Clear filters */}
            {activeFiltersCount > 0 && (
              <div className="flex justify-end">
                <button
                  onClick={clearFilters}
                  className="text-xs text-red-400 hover:text-red-300 transition-colors"
                >
                  נקה הכל
                </button>
              </div>
            )}
          </div>
        )}

        {/* Quick filters - Work Mode */}
        <div className="shrink-0 px-5 py-2 border-b border-gray-700 bg-gray-800 flex items-center gap-2 overflow-x-auto">
          <span className="text-xs text-gray-500 font-medium shrink-0">מצב עבודה:</span>
          <button
            onClick={() => applyWorkMode('high_priority')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${
              activeWorkMode === 'high_priority'
                ? 'bg-red-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            {'\uD83D\uDD25'} הכי חשובים
          </button>
          <button
            onClick={() => applyWorkMode('needs_action')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${
              activeWorkMode === 'needs_action'
                ? 'bg-orange-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            <Flame size={13} />
            דורש טיפול
          </button>
          <button
            onClick={() => applyWorkMode('no_contact')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${
              activeWorkMode === 'no_contact'
                ? 'bg-amber-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            <Phone size={13} />
            לא דיברת
          </button>
          <button
            onClick={() => applyWorkMode('action_required')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${
              activeWorkMode === 'action_required'
                ? 'bg-sky-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            <Zap size={13} />
            פעולה נדרשת
          </button>
          <button
            onClick={() => applyWorkMode('serious')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${
              activeWorkMode === 'serious'
                ? 'bg-emerald-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            <Target size={13} />
            רציניים
          </button>
          <button
            onClick={() => applyWorkMode('not_relevant')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${
              activeWorkMode === 'not_relevant'
                ? 'bg-red-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            <XCircle size={13} />
            לא רלוונטי
          </button>
        </div>

        {/* Leads list - single scrollable area taking remaining height */}
        <div className="flex-1 overflow-y-auto scrollbar-visible p-5">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500 mx-auto mb-3"></div>
                <p className="text-gray-400 text-sm">טוען לקוחות...</p>
              </div>
            </div>
          ) : sortedFilteredClients.length === 0 ? (
            <div className="text-center py-12">
              <Users size={48} className="mx-auto text-gray-600 mb-3" />
              <p className="text-lg text-gray-400 mb-1">
                {searchTerm || activeFiltersCount > 0 ? 'לא נמצאו לקוחות' : 'אין לקוחות עדיין'}
              </p>
              <p className="text-gray-500 text-sm mb-4">
                {searchTerm ? 'נסה לשנות את מונח החיפוש' : 'התחל להוסיף לקוחות כדי לנהל אותם'}
              </p>
              {!searchTerm && !activeFiltersCount && (
                <button
                  onClick={() => setShowAddModal(true)}
                  className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm"
                >
                  הוסף לקוח ראשון
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {sortedFilteredClients.map(client => (
                <div
                  key={client.id}
                  ref={(el) => (leadRefs.current[client.id] = el)}
                  className={`bg-gray-750 border rounded-lg p-4 hover:border-blue-500 transition-all ${
                    highlightedLeadId === client.id
                      ? 'border-blue-500 shadow-lg shadow-blue-500/50 animate-pulse'
                      : 'border-gray-700'
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h3 className="text-base font-bold text-white truncate">{client.name}</h3>
                        <span className={`shrink-0 px-2 py-0.5 rounded-full text-xs font-medium ${
                          client.leadType === 'buyer'
                            ? 'bg-blue-600 text-white'
                            : 'bg-orange-600 text-white'
                        }`}>
                          {client.leadType === 'buyer' ? 'קונה' : 'מוכר'}
                        </span>
                        {client.customerStatus && customerStatusLabels[client.customerStatus] && (
                          <span className={`shrink-0 px-2 py-0.5 rounded-full text-xs font-medium text-white ${customerStatusLabels[client.customerStatus].color}`}>
                            {customerStatusLabels[client.customerStatus].label}
                          </span>
                        )}
                        {(() => {
                          const score = computePriorityScore(client);
                          const priority = getPriorityLevel(score);
                          return (
                            <span className={`shrink-0 px-2 py-0.5 rounded-full text-xs font-medium ${priority.color}`}>
                              {score >= 50 ? '\uD83D\uDD25' : score >= 20 ? '\uD83D\uDFE1' : '\u26AA'} {priority.label}
                            </span>
                          );
                        })()}
                        {client.agentName && (
                          <span className="shrink-0 px-2 py-0.5 rounded-full text-xs font-medium bg-teal-900/60 text-teal-300 border border-teal-700/40">
                            {client.agentName}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-sm text-gray-300">
                        <span>{client.phone}</span>
                        {client.email && <span className="truncate">{client.email}</span>}
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <PhoneCall size={11} />
                          {formatLocalDate(client.lastContactAt) || 'לא נוצר קשר'}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock size={11} />
                          {formatLocalDate(client.nextFollowUpAt) || 'לא נקבע מעקב'}
                        </span>
                        <span className="text-gray-600">
                          נוצר: {new Date(client.createdAt).toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                        {client.nextAction && (
                          <span className="px-2 py-0.5 rounded bg-sky-700 text-sky-100 text-xs font-medium">
                            {client.nextAction}
                          </span>
                        )}
                        {client.nextFollowUpAt && !client.nextAction && (
                          <span className="px-2 py-0.5 rounded bg-amber-700 text-amber-100 text-xs font-medium">
                            חסרה פעולה למעקב
                          </span>
                        )}
                        {client.lastActivity && (
                          <span className="text-xs text-gray-400">
                            פעילות אחרונה: {formatLastActivity(client.lastActivity)}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-1.5 shrink-0 mr-2">
                      <button
                        onClick={() => setSelectedClient(client)}
                        className="p-1.5 bg-blue-600 hover:bg-blue-700 rounded transition-colors"
                        title={client.leadType === 'seller' ? 'צפה בקונים מתאימים' : 'צפה בנכסים מתאימים'}
                      >
                        <Eye size={15} />
                      </button>
                      {client.leadType === 'buyer' && (
                        <button
                          onClick={() => setSelectedClientForManual(client)}
                          className="p-1.5 bg-teal-600 hover:bg-teal-700 rounded transition-colors"
                          title="נכסים שהתאמתי ללקוח"
                        >
                          <Clipboard size={15} />
                        </button>
                      )}
                      {client.leadType === 'seller' && (
                        <button
                          onClick={() => setSelectedSeller(client)}
                          className="p-1.5 bg-orange-600 hover:bg-orange-700 rounded transition-colors"
                          title="צפה בנכסים של המוכר"
                        >
                          <Home size={15} />
                        </button>
                      )}
                      <button
                        onClick={() => handleMarkContacted(client.id)}
                        className="p-1.5 bg-green-600 hover:bg-green-700 rounded transition-colors"
                        title="סומן שנוצר קשר"
                      >
                        <PhoneCall size={15} />
                      </button>
                      <button
                        onClick={() => setActivityClient(client)}
                        className="p-1.5 bg-cyan-600 hover:bg-cyan-700 rounded transition-colors"
                        title="הוסף פעילות"
                      >
                        <FileText size={15} />
                      </button>
                      <button
                        onClick={() => setExpandedCardId(expandedCardId === client.id ? null : client.id)}
                        className={`p-1.5 rounded transition-colors ${expandedCardId === client.id ? 'bg-blue-500 hover:bg-blue-600' : 'bg-gray-600 hover:bg-gray-500'}`}
                        title="הגדל כרטיס"
                      >
                        <Maximize2 size={15} />
                      </button>
                      <button
                        onClick={() => {
                          setEditingClient(client);
                          setShowAddModal(true);
                        }}
                        className="p-1.5 bg-gray-600 hover:bg-gray-500 rounded transition-colors"
                        title="ערוך לקוח"
                      >
                        <Edit2 size={15} />
                      </button>
                      <button
                        onClick={() => handleDeleteClient(client.id)}
                        className="p-1.5 bg-red-600 hover:bg-red-700 rounded transition-colors"
                        title="מחק לקוח"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </div>

                  {/* Search criteria as compact chips */}
                  <div className="flex flex-wrap gap-1.5 text-xs">
                    {client.searchCriteria.priceMin && (
                      <span className="bg-gray-700 text-gray-200 px-2 py-0.5 rounded">
                        מ-₪{client.searchCriteria.priceMin.toLocaleString()}
                      </span>
                    )}
                    {client.searchCriteria.priceMax && (
                      <span className="bg-gray-700 text-gray-200 px-2 py-0.5 rounded">
                        עד ₪{client.searchCriteria.priceMax.toLocaleString()}
                      </span>
                    )}
                    {client.searchCriteria.rooms && client.searchCriteria.rooms.length > 0 && (
                      <span className="bg-gray-700 text-gray-200 px-2 py-0.5 rounded">
                        {client.searchCriteria.rooms.join(',')} חד'
                      </span>
                    )}
                    {client.searchCriteria.cities && client.searchCriteria.cities.length > 0 && (
                      <span className="bg-gray-700 text-gray-200 px-2 py-0.5 rounded">
                        {client.searchCriteria.cities.slice(0, 3).join(', ')}
                        {client.searchCriteria.cities.length > 3 && ` +${client.searchCriteria.cities.length - 3}`}
                      </span>
                    )}
                    {client.searchCriteria.propertyTypes && client.searchCriteria.propertyTypes.length > 0 && (
                      <span className="bg-gray-700 text-gray-200 px-2 py-0.5 rounded">
                        {client.searchCriteria.propertyTypes
                          .map(t => propertyTypes.find(pt => pt.value === t)?.label || t)
                          .slice(0, 2)
                          .join(', ')}
                        {client.searchCriteria.propertyTypes.length > 2 && ` +${client.searchCriteria.propertyTypes.length - 2}`}
                      </span>
                    )}
                  </div>

                  {client.notes && (
                    <p className="mt-2 text-xs text-gray-400 truncate">{client.notes}</p>
                  )}

                  {(() => {
                    const rec = getLeadRecommendation(client);
                    if (!rec) return null;
                    return (
                      <div className="mt-2 flex items-center gap-2 bg-gray-700/40 rounded px-2 py-1">
                        <span className="text-xs">🤖</span>
                        <span className="text-xs text-sky-300 truncate flex-1">{rec.short}</span>
                        <button
                          onClick={(e) => { e.stopPropagation(); openMessageConfig(client); }}
                          className="shrink-0 text-[10px] px-1.5 py-0.5 rounded bg-sky-700 text-sky-100 hover:bg-sky-600 transition-colors"
                          title="צור הודעה"
                        >
                          ✉️ צור הודעה
                        </button>
                      </div>
                    );
                  })()}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {showAddModal && (
        <AddClientModal
          client={editingClient}
          onClose={() => {
            setShowAddModal(false);
            setEditingClient(null);
          }}
          onSave={() => {
            setShowAddModal(false);
            setEditingClient(null);
            loadClients();
          }}
        />
      )}

      {expandedCardId && (() => {
        const expandedClient = clients.find(c => c.id === expandedCardId);
        return expandedClient ? (
          <FloatingClientCard
            client={expandedClient}
            onClose={() => setExpandedCardId(null)}
            onGenerateMessage={openMessageConfig}
          />
        ) : null;
      })()}

      {messageGenConfig && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60" dir="rtl">
          <div className="bg-gray-800 border border-gray-700 rounded-xl shadow-2xl w-full max-w-sm mx-4">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700">
              <h3 className="text-base font-bold text-white">הגדרות הודעה</h3>
              <button onClick={() => setMessageGenConfig(null)} className="p-1.5 rounded hover:bg-gray-700 text-gray-400 hover:text-white transition-colors">
                <X size={16} />
              </button>
            </div>

            <div className="px-5 pt-4 space-y-4">
              {/* Gender selector */}
              <div>
                <label className="text-xs text-gray-400 mb-2 block">פנייה:</label>
                <div className="grid grid-cols-3 gap-2">
                  {([['neutral', 'ניטרלי'], ['male', 'זכר'], ['female', 'נקבה']] as const).map(([val, label]) => (
                    <button
                      key={val}
                      onClick={() => setMessageGenConfig(prev => prev ? { ...prev, gender: val } : null)}
                      className={`py-2 rounded-lg text-sm font-medium transition-colors ${
                        messageGenConfig.gender === val
                          ? 'bg-sky-600 text-white'
                          : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Seller property selector */}
              {messageGenConfig.client.leadType === 'seller' && (
                <div>
                  <label className="text-xs text-gray-400 mb-2 block">נכס קשור להודעה:</label>
                  {messageGenConfig.loadingProperties ? (
                    <div className="flex items-center justify-center py-4 gap-2 text-gray-500">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-sky-500 border-t-transparent" />
                      <span className="text-xs">טוען נכסים...</span>
                    </div>
                  ) : messageGenConfig.sellerProperties.length === 0 ? (
                    <p className="text-xs text-gray-500 text-center py-2.5 bg-gray-700/50 rounded-lg">
                      לא נמצאו נכסים מקושרים למוכר
                    </p>
                  ) : (
                    <div className="space-y-1.5 max-h-44 overflow-y-auto">
                      {messageGenConfig.sellerProperties.map(prop => {
                        const isSelected = messageGenConfig.selectedFormId === prop.formId;
                        const addr = [prop.address?.street, prop.address?.houseNumber, prop.address?.city].filter(Boolean).join(' ');
                        return (
                          <button
                            key={prop.formId}
                            onClick={() => setMessageGenConfig(prev => prev ? { ...prev, selectedFormId: isSelected ? null : prop.formId } : null)}
                            className={`w-full text-right px-3 py-2 rounded-lg transition-colors border text-sm ${
                              isSelected
                                ? 'bg-sky-900/40 border-sky-500 text-sky-200'
                                : 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600 hover:border-gray-500'
                            }`}
                          >
                            <div className="font-medium">{addr || '—'}</div>
                            <div className="text-xs text-gray-400 mt-0.5">
                              {prop.property?.price ? `₪${prop.property.price.toLocaleString()}` : ''}
                              {prop.property?.rooms ? ` · ${prop.property.rooms} חד'` : ''}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex gap-2 px-5 py-4 mt-2 border-t border-gray-700">
              <button
                onClick={() => setMessageGenConfig(null)}
                className="flex-1 py-2 text-sm rounded-lg bg-gray-700 text-gray-300 hover:bg-gray-600 transition-colors"
              >
                ביטול
              </button>
              <button
                onClick={() => {
                  const cfg = messageGenConfig;
                  setMessageGenConfig(null);
                  const selectedProp = cfg.sellerProperties.find(p => p.formId === cfg.selectedFormId) ?? null;
                  handleGenerateMessage(cfg.client, cfg.gender, selectedProp);
                }}
                disabled={messageGenConfig.loadingProperties}
                className="flex-1 py-2 text-sm rounded-lg bg-sky-600 hover:bg-sky-700 disabled:opacity-50 text-white font-medium transition-colors"
              >
                צור הודעה
              </button>
            </div>
          </div>
        </div>
      )}

      {generatingMessageFor && (
        <GeneratingModal leadName={generatingMessageFor.name} />
      )}

      {messageDraft && (
        <MessageDraftModal
          draft={messageDraft.draft}
          leadName={messageDraft.leadName}
          onClose={() => setMessageDraft(null)}
          onRegenerate={() => {
            setMessageDraft(null);
            openMessageConfig(messageDraft.client);
          }}
        />
      )}

      {activityClient && (
        <AddActivityModal
          leadId={activityClient.id}
          leadName={activityClient.name}
          onClose={() => setActivityClient(null)}
          onSaved={() => {
            setActivityClient(null);
            loadClients();
          }}
        />
      )}

      {selectedClient && (
        <ClientPropertiesModal
          client={selectedClient}
          onClose={() => setSelectedClient(null)}
        />
      )}

      {selectedClientForManual && (
        <ManuallyMatchedPropertiesModal
          client={selectedClientForManual}
          onClose={() => setSelectedClientForManual(null)}
        />
      )}

      {selectedSeller && (
        <SellerPropertiesModal
          seller={selectedSeller}
          onClose={() => setSelectedSeller(null)}
        />
      )}
    </div>
  );
};

export default ClientCRM;
